import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
  type Firestore,
  type QueryConstraint,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "./firebase";
import type { PayrollDoc, Registration, WeekDoc, WeekStatus } from "./types";
import { formatWeekId, getCurrentOrLastMonday } from "./format";

function tsToMillis(value: unknown): number {
  if (value && typeof value === "object" && "toMillis" in (value as Record<string, unknown>)) {
    return (value as { toMillis: () => number }).toMillis();
  }
  if (typeof value === "number") return value;
  return Date.now();
}

/** Returns the most recent open week, or the most recent week overall. */
export async function getCurrentWeek(firestore: Firestore = db): Promise<WeekDoc | null> {
  const monday = getCurrentOrLastMonday();
  const weekId = formatWeekId(monday);
  const snap = await getDoc(doc(firestore, "weeks", weekId));
  if (snap.exists()) {
    const d = snap.data() as Record<string, unknown>;
    return {
      label: d.label as string,
      startDate: d.startDate as string,
      endDate: d.endDate as string,
      status: d.status as WeekStatus,
      createdAt: tsToMillis(d.createdAt),
    };
  }
  return null;
}

/** Returns all weeks, most recent first. Mirrors the manager Dashboard listing. */
export async function listWeeks(firestore: Firestore = db): Promise<WeekDoc[]> {
  const q = query(collection(firestore, "weeks"), orderBy("startDate", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data() as Record<string, unknown>;
    return {
      label: data.label as string,
      startDate: data.startDate as string,
      endDate: data.endDate as string,
      status: data.status as WeekStatus,
      createdAt: tsToMillis(data.createdAt),
    };
  });
}

export async function getRegistration(
  weekId: string,
  phone: string,
  firestore: Firestore = db,
): Promise<Registration | null> {
  const ref = doc(firestore, "weeks", weekId, "registrations", phone);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const d = snap.data() as Record<string, unknown>;
  return {
    phone: d.phone as string,
    name: d.name as string,
    updatedAt: tsToMillis(d.updatedAt),
    shifts: (d.shifts as Registration["shifts"]) ?? [],
  };
}

/** Returns the official payroll record for an employee in a week, or null if not yet calculated. */
export async function getPayroll(
  weekId: string,
  phone: string,
  firestore: Firestore = db,
): Promise<PayrollDoc | null> {
  const ref = doc(firestore, "weeks", weekId, "payroll", phone);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const d = snap.data() as Record<string, unknown>;
  return {
    phone: d.phone as string,
    name: d.name as string,
    totalHours: d.totalHours as number,
    totalPay: d.totalPay as number,
    hourlyRate: d.hourlyRate as number,
    shifts: (d.shifts as Registration["shifts"]) ?? [],
    payslipUrl: (d.payslipUrl as string | null) ?? null,
    calculatedAt: tsToMillis(d.calculatedAt),
  };
}

export async function saveRegistration(
  weekId: string,
  registration: Registration,
  firestore: Firestore = db,
): Promise<void> {
  const ref = doc(firestore, "weeks", weekId, "registrations", registration.phone);
  await setDoc(ref, {
    ...registration,
    updatedAt: new Date(),
  });
}

export function subscribeToRegistrations(
  weekId: string,
  onChange: (regs: Registration[]) => void,
  onError?: (err: Error) => void,
  firestore: Firestore = db,
): Unsubscribe {
  const regsRef = collection(firestore, "weeks", weekId, "registrations");
  return onSnapshot(
    regsRef,
    (snap) => {
      onChange(
        snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          return {
            phone: data.phone as string,
            name: data.name as string,
            updatedAt: tsToMillis(data.updatedAt),
            shifts: (data.shifts as Registration["shifts"]) ?? [],
          };
        }),
      );
    },
    onError,
  );
}

export { where, query, type QueryConstraint };
